import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useRestApi } from "@/context/api";
import { useCurrentCompany } from "@/context/auth";
import { AccessRequestStatus } from "@/types/auth";

import { queryKeys } from "./queryKeys";

const ACCESS_REQUEST_TOKEN_KEY = "accessRequestToken";

export const useClientCompaniesForRequestQuery = () => {
  const { getClientCompanies } = useRestApi();

  return useQuery({
    queryKey: [queryKeys.ACCESS_REQUESTS, "clientCompanies"],
    queryFn: () => getClientCompanies(),
    staleTime: 60000,
  });
};

export const useApproversQuery = (companyId: string | undefined) => {
  const { getApprovers } = useRestApi();

  return useQuery({
    queryKey: [queryKeys.ACCESS_REQUESTS, "approvers", companyId],
    queryFn: () => getApprovers(companyId!),
    enabled: !!companyId,
    staleTime: 30000,
  });
};

export const useValidateTargetUserMutation = () => {
  const { validateTargetUser } = useRestApi();

  return useMutation({
    mutationFn: (args: { companyId: string; email: string }) =>
      validateTargetUser(args.companyId, args.email),
  });
};

export const JIRA_ISSUE_KEY_PATTERN = /^DCS-\d+$/;

export const useJiraIssueQuery = (key: string) => {
  const { getJiraIssue } = useRestApi().accessRequests;

  return useQuery({
    queryKey: [queryKeys.ACCESS_REQUESTS, "jiraIssue", key],
    queryFn: () => getJiraIssue(key),
    enabled: JIRA_ISSUE_KEY_PATTERN.test(key),
    retry: false,
    staleTime: 5 * 60000,
  });
};

export const useAccessRequestsQuery = (
  statuses?: AccessRequestStatus[],
  options?: { enabled?: boolean },
) => {
  const { getAccessRequests } = useRestApi().accessRequests;
  const currentCompany = useCurrentCompany();

  return useQuery({
    queryKey: [queryKeys.ACCESS_REQUESTS, currentCompany.id, statuses],
    queryFn: () => getAccessRequests(currentCompany.id, statuses),
    staleTime: 30000,
    enabled: options?.enabled,
  });
};

export const useMyAccessRequestsQuery = (
  statuses?: AccessRequestStatus[],
  options?: { enabled?: boolean },
) => {
  const { getMyAccessRequests } = useRestApi().accessRequests;

  return useQuery({
    queryKey: [queryKeys.ACCESS_REQUESTS, "own", statuses],
    queryFn: () => getMyAccessRequests(statuses),
    staleTime: 30000,
    enabled: options?.enabled,
  });
};

export const useAccessRequestSecurityLogsQuery = (requestId: string | null) => {
  const { getAccessRequestSecurityLogs } = useRestApi().accessRequests;

  return useQuery({
    queryKey: [queryKeys.ACCESS_REQUESTS, "securityLogs", requestId],
    queryFn: () => getAccessRequestSecurityLogs(requestId!),
    enabled: !!requestId,
    staleTime: 30000,
  });
};

export const useCreateAccessRequestMutation = () => {
  const { createAccessRequest } = useRestApi().accessRequests;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (args: {
      targetUserId: string;
      justification?: string;
      approverId?: string;
      jiraIssueKey?: string;
    }) =>
      createAccessRequest(
        args.targetUserId,
        args.justification,
        args.approverId,
        args.jiraIssueKey,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ACCESS_REQUESTS],
        refetchType: "active",
      });
    },
  });
};

export const useActivateAccessRequestMutation = () => {
  const { activateAccessRequest } = useRestApi().accessRequests;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => activateAccessRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ACCESS_REQUESTS],
        refetchType: "active",
      });
    },
  });
};

export const useResumeAccessRequestMutation = () => {
  const { resumeAccessRequest } = useRestApi().accessRequests;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => resumeAccessRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ACCESS_REQUESTS],
        refetchType: "active",
      });
    },
  });
};

export const useRevokeAccessRequestMutation = () => {
  const { revokeAccessRequest } = useRestApi().accessRequests;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => revokeAccessRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ACCESS_REQUESTS],
        refetchType: "active",
      });
    },
  });
};

export const useCancelAccessRequestMutation = () => {
  const { cancelAccessRequest } = useRestApi().accessRequests;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => cancelAccessRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ACCESS_REQUESTS],
        refetchType: "active",
      });
    },
  });
};

export const useCompleteAccessRequestMutation = () => {
  const { completeAccessRequest } = useRestApi().accessRequests;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => completeAccessRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ACCESS_REQUESTS],
        refetchType: "active",
      });
    },
  });
};

export const useValidateAccessRequestTokenQuery = (token: string | null) => {
  const { validateAccessRequestToken } = useRestApi().accessRequests;

  return useQuery({
    queryKey: [ACCESS_REQUEST_TOKEN_KEY, token],
    queryFn: () => validateAccessRequestToken(token!),
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
  });
};

// Token-based mutations (from email link, target user)
export const useApproveAccessRequestByTokenMutation = () => {
  const { approveAccessRequestByToken } = useRestApi().accessRequests;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (token: string) => approveAccessRequestByToken(token),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ACCESS_REQUESTS],
        refetchType: "active",
      });
    },
  });
};

export const useDenyAccessRequestByTokenMutation = () => {
  const { denyAccessRequestByToken } = useRestApi().accessRequests;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (token: string) => denyAccessRequestByToken(token),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ACCESS_REQUESTS],
        refetchType: "active",
      });
    },
  });
};

// ID-based mutations (from admin panel)
export const useApproveAccessRequestMutation = () => {
  const { approveAccessRequestById } = useRestApi().accessRequests;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => approveAccessRequestById(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ACCESS_REQUESTS],
        refetchType: "active",
      });
    },
  });
};

export const useDenyAccessRequestMutation = () => {
  const { denyAccessRequestById } = useRestApi().accessRequests;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => denyAccessRequestById(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ACCESS_REQUESTS],
        refetchType: "active",
      });
    },
  });
};
